import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildGroupTree,
  filterGroupsForSearch,
  type DrawingGroup,
  type GroupTreeNode,
} from '@system-atlas/shared';
import { TextInput } from './ui';
import { cn } from '../lib/utils';

const STORAGE_KEY = 'system-atlas.group-tree-expanded';

function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * Rekursiver Gruppen-Baum mit Suche, Auf-/Zuklappen (persistiert in
 * localStorage) und Hover-Aktionen je Gruppe. Bei aktiver Suche werden
 * Treffer-Pfade automatisch aufgeklappt.
 */
export function GroupTree({
  groups,
  selectedId,
  onSelect,
  onCreateChild,
  onRename,
  onMove,
  onDelete,
  onShare,
}: {
  groups: DrawingGroup[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreateChild: (group: DrawingGroup) => void;
  onRename: (group: DrawingGroup) => void;
  onMove: (group: DrawingGroup) => void;
  onDelete: (group: DrawingGroup) => void;
  onShare: (group: DrawingGroup) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(loadExpanded);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
    } catch {
      // localStorage nicht verfügbar – Zustand gilt dann nur für die Sitzung.
    }
  }, [expanded]);

  const tree = useMemo(() => buildGroupTree(groups), [groups]);
  const visible = useMemo(() => filterGroupsForSearch(groups, search), [groups, search]);
  const searching = search.trim().length > 0;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const actionClass =
    'rounded px-1 text-slate-400 opacity-0 transition-opacity hover:text-slate-700 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:text-slate-200';

  const renderNode = (node: GroupTreeNode<DrawingGroup>, depth: number): ReactElement | null => {
    if (!visible.has(node.group.id)) return null;
    const children = node.children.filter((c) => visible.has(c.group.id));
    const isOpen = searching || expanded.has(node.group.id);
    return (
      <div key={node.group.id}>
        <div className="group flex items-center" style={{ paddingLeft: `${depth * 14}px` }}>
          {children.length > 0 ? (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={isOpen ? t('groups.collapse') : t('groups.expand')}
              onClick={() => toggle(node.group.id)}
              className="w-5 shrink-0 rounded text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              {isOpen ? '▾' : '▸'}
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => onSelect(node.group.id)}
            className={cn(
              'min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm',
              selectedId === node.group.id
                ? 'bg-slate-200 font-medium dark:bg-slate-700'
                : 'hover:bg-slate-200/60 dark:hover:bg-slate-700/60',
            )}
          >
            {node.group.name}
          </button>
          {node.group.owned && (
            <>
              <button
                type="button"
                title={t('groups.addSubgroup')}
                aria-label={t('groups.addSubgroup')}
                onClick={() => onCreateChild(node.group)}
                className={actionClass}
              >
                +
              </button>
              <button
                type="button"
                title={t('groups.rename')}
                aria-label={t('groups.rename')}
                onClick={() => onRename(node.group)}
                className={actionClass}
              >
                ✎
              </button>
              <button
                type="button"
                title={t('groups.move')}
                aria-label={t('groups.move')}
                onClick={() => onMove(node.group)}
                className={actionClass}
              >
                ↪
              </button>
              <button
                type="button"
                title={t('groups.share')}
                aria-label={t('groups.share')}
                onClick={() => onShare(node.group)}
                className={actionClass}
              >
                ⤴
              </button>
              <button
                type="button"
                title={t('groups.delete')}
                aria-label={t('groups.delete')}
                onClick={() => onDelete(node.group)}
                className={cn(actionClass, 'hover:text-red-600 dark:hover:text-red-400')}
              >
                ×
              </button>
            </>
          )}
        </div>
        {isOpen && children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-1">
      <TextInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('groups.searchPlaceholder')}
        className="mb-1 text-sm"
      />
      {tree.map((node) => renderNode(node, 0))}
      {searching && visible.size === 0 && (
        <p className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400">{t('groups.noMatches')}</p>
      )}
    </div>
  );
}
